import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Trophy,
  User,
  Calendar,
  Target,
} from "lucide-react";
import TournamentCard from "../utils/home/custom_card";
import BottomNavBar from "../components/BottomNavBar";
import logo from "../assets/wito.png";

const Home = () => {
  // Redux
  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  // State
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Navegación slides
  const totalSlides = 2;

  const nextSlide = () => {
    if (!homeData) return;
    setCurrentSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (!homeData) return;
    setCurrentSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  // Lógica de agrupación de jugadores
  const playerList = homeData?.highlightedPlayers
    ? Object.values(
        homeData.highlightedPlayers.reduce((acc, p) => {
          const key = `${p.player_id}-${p.latest_match}`;
          if (!acc[key]) {
            acc[key] = { ...p, goals: 0 };
          }
          acc[key].goals++;
          return acc;
        }, {}),
      )
    : [];

  // Torneos
  const tournaments = homeData?.featuredTournaments || [];

  // Fetch home data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch("https://server-postgre.vercel.app/api/home");
        const data = await res.json();

        setHomeData(data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- Sub-componentes para limpieza visual ---

  // Tarjeta de Partido
  const MatchCard = ({ match }) => (
    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm mb-3 hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-center mb-3">
        <span className="text-[10px] font-bold tracking-wide text-gray-400 uppercase">
          {match.status === "completed" ? "Finalizado" : "Programado"}
        </span>
        <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
          {new Date().toLocaleDateString()}
        </span>
      </div>

      <div className="flex justify-between items-center">
        {/* Equipo 1 */}
        <div className="flex flex-col items-center flex-1">
          {match.team1_logo ? (
            <img
              src={match.team1_logo}
              className="w-12 h-12 object-contain mb-2 drop-shadow-sm"
              alt={match.team1_name}
            />
          ) : (
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mb-2">
              <Trophy size={20} className="text-gray-400" />
            </div>
          )}
          <p className="text-xs font-semibold text-center text-gray-700 line-clamp-1 w-full px-1">
            {match.team1_name}
          </p>
        </div>

        {/* Marcador */}
        <div className="flex flex-col items-center px-4">
          {match.status === "completed" ? (
            <div className="text-2xl font-black text-gray-800 tracking-tighter bg-gray-100 px-4 py-1 rounded-lg">
              {match.score_team1} - {match.score_team2}
            </div>
          ) : (
            <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
              VS
            </span>
          )}
        </div>

        {/* Equipo 2 */}
        <div className="flex flex-col items-center flex-1">
          {match.team2_logo ? (
            <img
              src={match.team2_logo}
              className="w-12 h-12 object-contain mb-2 drop-shadow-sm"
              alt={match.team2_name}
            />
          ) : (
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mb-2">
              <Trophy size={20} className="text-gray-400" />
            </div>
          )}
          <p className="text-xs font-semibold text-center text-gray-700 line-clamp-1 w-full px-1">
            {match.team2_name}
          </p>
        </div>
      </div>
    </div>
  );

  // Tarjeta combinada Goleador + Partido
  const PlayerWithMatchCard = ({ player }) => (
    <div className="flex bg-white border border-gray-100 rounded-xl p-3 shadow-sm mb-3 hover:shadow-md transition-all">
      {/* IZQUIERDA: Jugador */}
      <div className="flex flex-col items-center flex-1 gap-2">
        {/* Foto del jugador */}
        <div className="relative w-16 h-16">
          {player.profile_picture_url ? (
            <img
              src={player.profile_picture_url}
              className="w-16 h-16 rounded-full object-cover border-2 border-emerald-100"
              alt="Jugador"
            />
          ) : (
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 border-2 border-emerald-100">
              <User size={24} className="text-gray-400" />
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-white">
            #{player.subcategory_number || "10"}
          </div>
        </div>

        {/* Nombre del jugador */}
        <h4 className="text-sm font-bold text-gray-900 truncate">
          {player.first_name} {player.last_name}
        </h4>

        {/* Equipo del jugador con logo */}
        <div className="flex items-center gap-1">
          {player.player_team_logo ? (
            <img
              src={player.player_team_logo}
              alt={player.player_team_name}
              className="w-5 h-5 object-contain rounded-full"
            />
          ) : (
            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100">
              <Trophy size={12} className="text-gray-400" />
            </div>
          )}
          <span className="text-xs text-gray-500 truncate">
            {player.player_team_name}
          </span>
        </div>

        {/* Goles */}
        <div className="flex items-center mt-1">
          <div className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg">
            <Target size={14} className="stroke-2" />
            <span className="text-sm font-bold">{player.goals_in_match}</span>
          </div>
          <span className="text-[10px] text-gray-400 mt-1">Goles</span>
        </div>
      </div>

      {/* DERECHA: Partido */}
      <div className="flex-1 flex flex-col justify-center pl-4 border-l border-gray-100">
        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">
          {player.tournament_name}
        </p>
        <p className="text-[10px] text-gray-400 mb-2">
          {new Date(player.match_date).toLocaleDateString()} •{" "}
          {player.status === "completed" ? "Finalizado" : "Programado"}
        </p>

        <div className="flex justify-between items-center">
          {/* Equipo 1 */}
          <div className="flex flex-col items-center flex-1">
            {player.team1_logo ? (
              <img
                src={player.team1_logo}
                className="w-10 h-10 object-contain mb-1"
                alt={player.team1_name}
              />
            ) : (
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 mb-1">
                <Trophy size={16} className="text-gray-400" />
              </div>
            )}
            <p className="text-[10px] text-center truncate">
              {player.team1_name}
            </p>
          </div>

          {/* Marcador */}
          <div className="px-2">
            <div className="text-sm font-bold text-gray-800">
              {player.score_team1} - {player.score_team2}
            </div>
          </div>

          {/* Equipo 2 */}
          <div className="flex flex-col items-center flex-1">
            {player.team2_logo ? (
              <img
                src={player.team2_logo}
                className="w-10 h-10 object-contain mb-1"
                alt={player.team2_name}
              />
            ) : (
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 mb-1">
                <Trophy size={16} className="text-gray-400" />
              </div>
            )}
            <p className="text-[10px] text-center truncate">
              {player.team2_name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen font-sans pb-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        body { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Header Moderno */}
      <header className="relative p-6 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 text-white rounded-b-[2.5rem] shadow-xl shadow-emerald-200 overflow-hidden">
        {/* Decoración de fondo */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-y-10 translate-x-10 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-black opacity-5 rounded-full translate-y-10 -translate-x-5 blur-xl"></div>

        <div className="relative z-10 flex justify-between items-center mb-6">
          <img
            src={logo}
            alt="Logo Wito"
            className="h-8 w-auto drop-shadow-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://placehold.co/100x32/FFFFFF/10b981?text=WITO";
            }}
          />
        </div>

        <div className="relative z-10">
          <h1 className="text-3xl font-extrabold tracking-tight">
            Hola,{" "}
            <span className="text-emerald-100">
              {isLoggedIn && user?.first_name ? user.first_name : "Atleta"}
            </span>
          </h1>
          <p className="opacity-90 mt-1 font-medium text-emerald-50 flex items-center gap-2">
            <Calendar size={14} /> El deporte te espera hoy
          </p>
        </div>
      </header>

      {/* Acciones rápidas (Floating Cards) */}
      <section className="px-6 -mt-4 relative z-20">
        <div className="bg-white p-2 rounded-2xl shadow-lg shadow-gray-200/50 flex justify-around border border-gray-100">
          <a
            href="/create-team"
            className="group flex flex-col items-center justify-center p-3 rounded-xl hover:bg-blue-50 transition-colors flex-1"
          >
            <div className="bg-blue-100 text-blue-600 p-3 rounded-xl mb-2 group-hover:scale-110 transition-transform">
              <PlusCircle size={22} strokeWidth={2.5} />
            </div>
            <span className="text-xs font-bold text-gray-600 group-hover:text-blue-600">
              Crear Equipo
            </span>
          </a>

          <div className="w-px bg-gray-100 my-2"></div>

          <a
            href="/create-tournament"
            className="group flex flex-col items-center justify-center p-3 rounded-xl hover:bg-emerald-50 transition-colors flex-1"
          >
            <div className="bg-emerald-100 text-emerald-600 p-3 rounded-xl mb-2 group-hover:scale-110 transition-transform">
              <Trophy size={22} strokeWidth={2.5} />
            </div>
            <span className="text-xs font-bold text-gray-600 group-hover:text-emerald-600">
              Crear Torneo
            </span>
          </a>
        </div>
      </section>

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 mt-8 px-6">
        {/* --- SECCIÓN CARRUSEL MODERNIZADA --- */}
        <section className="flex-1 w-full lg:max-w-md mx-auto">
          {loading && (
            <div className="animate-pulse flex space-x-4">
              <div className="flex-1 space-y-4 py-1">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-24 bg-gray-200 rounded-xl"></div>
                  <div className="h-24 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            </div>
          )}

          {!loading && homeData && (
            <div className="relative group">
              {/* Header del Carrusel con Título Dinámico */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  {currentSlide === 0 ? (
                    <>
                      <Trophy className="text-emerald-500" size={20} />
                      <span>Partidos Destacados</span>
                    </>
                  ) : (
                    <>
                      <Target className="text-blue-500" size={20} />
                      <span>Goleadores Top</span>
                    </>
                  )}
                </h3>

                {/* Controles del Carrusel (Pequeños y modernos) */}
                <div className="flex gap-1 bg-white border rounded-full p-1 shadow-sm">
                  <button
                    onClick={prevSlide}
                    className="p-1 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="p-1 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              {/* Contenedor del Slide */}
              <div className="relative w-full overflow-hidden min-h-[300px]">
                <div
                  className="transition-opacity duration-300 ease-in-out"
                  key={currentSlide} // Fuerza re-render para animación simple
                >
                  {/* Slide 0: Torneos y Partidos */}
                  {currentSlide === 0 && (
                    <div className="space-y-6">
                      {tournaments.length === 0 ? (
                        <div className="text-center py-10 text-gray-400">
                          No hay torneos activos
                        </div>
                      ) : (
                        tournaments.map((tournament) => (
                          <div key={tournament.id} className="last:mb-0">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">
                              {tournament.tournament_name}
                            </p>
                            <div className="flex flex-col gap-1">
                              {tournament.matches.map((m) => (
                                <MatchCard key={m.match_id} match={m} />
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Slide 1: Goleadores */}
                  {currentSlide === 1 && (
                    <div>
                      {playerList.length === 0 ? (
                        <div className="text-center py-10 text-gray-400">
                          Aún no hay goles registrados
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-1">
                          {playerList.map((p) => (
                            <PlayerWithMatchCard
                              key={`${p.player_id}-${p.match_id}`}
                              player={p}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Dots indicadores */}
              <div className="flex justify-center gap-2 mt-4">
                {[0, 1].map((idx) => (
                  <div
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                      idx === currentSlide
                        ? "w-6 bg-emerald-500"
                        : "w-1.5 bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* --- SECCIÓN LATERAL: TUS TORNEOS --- */}
        <section className="flex-1 w-full lg:max-w-md mx-auto">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-l-4 border-emerald-500 pl-3">
            {isLoggedIn ? "Siguiendo" : "Torneos Populares"}
          </h3>

          <div className="space-y-4">
            {isLoggedIn && user?.followedTournaments?.length > 0 ? (
              user.followedTournaments.map((t) => (
                <div
                  key={t.id}
                  className="transform transition-transform hover:-translate-y-1"
                >
                  <TournamentCard tournament={t} />
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center p-8 bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center shadow-sm">
                <div className="bg-gray-50 p-4 rounded-full mb-3">
                  <Trophy size={24} className="text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium mb-1">
                  {isLoggedIn
                    ? "Tu lista de seguimiento está vacía."
                    : "Inicia sesión para personalizar tu feed."}
                </p>
                <a
                  href="/tournaments"
                  className="mt-2 text-sm text-emerald-600 font-bold hover:text-emerald-700 hover:underline"
                >
                  Descubrir torneos →
                </a>
              </div>
            )}
          </div>
        </section>
      </div>

      <BottomNavBar />
    </div>
  );
};

export default Home;
